import { isAxiosError } from 'axios';
import { useRef, useState } from 'react';
import { useSecureBeforeYouClick as useSecureBeforeYouClickMutation } from '@data';
import { TIMING_METRICS } from '@constants';
import { useShareMailboxContext, useToastsContext } from '@utils';
import {
  encodeBase64,
  getUserEmail,
  handleError,
  logInfo,
  handleOfficeError,
  isRequestTimeout,
  SecureBeforeYouClickError,
} from '@utils-global';
import { useTimingMetric } from '../../../hooks/useTimingMetric.hook';
import { fetchSecureBeforeYouClickMime } from './fetchSecureBeforeYouClickMime';

type SecureBeforeYouClickFailureCause =
  | 'notReachable' // Never got a response, corporate firewall/proxy might silently block
  | 'timeout' // Reached the server but no answer within the client timeout, retrying may help
  | 'notEnabled' // 404 - this recipient's configuration doesn't have the feature enabled
  | 'paymentRequired' // 402 - the organization is out of credits
  | 'unknown'; // anything else (validation, 5xx, or a non-axios error)

type SecureBeforeYouClickErrorDiagnosis = {
  cause: SecureBeforeYouClickFailureCause;
  statusCode?: number;
  responseBody?: unknown;
};

const TOAST_KEY_BY_CAUSE: Record<SecureBeforeYouClickFailureCause, string> = {
  notReachable: 'errors.secureBeforeYouClick.notReachable',
  timeout: 'errors.secureBeforeYouClick.timeout',
  notEnabled: 'errors.secureBeforeYouClick.notEnabled',
  paymentRequired: 'errors.secureBeforeYouClick.paymentRequired',
  unknown: 'errors.secureBeforeYouClick.error',
};

const diagnoseSecureBeforeYouClickError = (error: unknown): SecureBeforeYouClickErrorDiagnosis => {
  if (!isAxiosError(error)) {
    return { cause: 'unknown' };
  }

  if (error.code === 'ERR_NETWORK') {
    return { cause: 'notReachable' };
  }

  if (isRequestTimeout(error)) {
    return { cause: 'timeout' };
  }

  const statusCode = error.response?.status;
  const responseBody = error.response?.data;

  if (statusCode === 404) {
    return { cause: 'notEnabled', statusCode, responseBody };
  }

  if (statusCode === 402) {
    return { cause: 'paymentRequired', statusCode, responseBody };
  }

  return { cause: 'unknown', statusCode, responseBody };
};

export const useSecureBeforeYouClick = () => {
  const { mutate: secureBeforeYouClick, isPending: isRequestPending } =
    useSecureBeforeYouClickMutation();
  // The MIME fetch runs before the request starts, so the mutation's own pending state misses it.
  // The ref turns a second click away at once; the state shows the button as busy meanwhile.
  const isPreparingRef = useRef(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const { showToast } = useToastsContext();
  const { isSharedMailbox, sharedProperties } = useShareMailboxContext();
  const { startTimingMetric, stopTimingMetric } = useTimingMetric();

  const mailboxEmail =
    isSharedMailbox && sharedProperties?.targetMailbox
      ? sharedProperties.targetMailbox
      : getUserEmail();

  /** Resolves once the request has been sent, or the click has been turned away. */
  const handleSecureBeforeYouClick = async (): Promise<void> => {
    if (isPreparingRef.current || isRequestPending) {
      return;
    }

    isPreparingRef.current = true;
    setIsPreparing(true);

    try {
      startTimingMetric(TIMING_METRICS.SBYC_DURATION);
      const { item } = Office.context.mailbox;

      if (!item) {
        showToast('errors.secureBeforeYouClick.noEmailItem');
        return;
      }

      // Get synchronous email properties
      const senderEmail = item.from?.emailAddress || item.sender?.emailAddress;
      const senderName = item.from?.displayName || item.sender?.displayName;

      const recipientEmail = Office.context.mailbox.userProfile.emailAddress;
      const recipientName = Office.context.mailbox.userProfile.displayName;

      const date = item.dateTimeCreated?.toISOString();
      const subject = item?.normalizedSubject || item?.subject;

      logInfo('Secure Before You Click', { senderEmail, recipientEmail, date });

      if (!senderEmail || !recipientEmail || !date) {
        showToast('errors.secureBeforeYouClick.missingEmailData');
        return;
      }

      const body = await new Promise<string | undefined>((resolve) => {
        item.body.getAsync(Office.CoercionType.Html, (result) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            handleOfficeError(result.error, {
              operation: 'Get email body',
            });

            showToast('errors.secureBeforeYouClick.getEmailBody');
            resolve(undefined);
            return;
          }

          if (!result.value) {
            showToast('errors.secureBeforeYouClick.emptyEmailBody');
          }

          resolve(result.value || undefined);
        });
      });

      if (!body) {
        return;
      }

      // Neither the body nor the MIME is logged: both are the email itself.
      const encodedBody = encodeBase64(`<body>${body}</body>`);
      const mime = await fetchSecureBeforeYouClickMime({ mailboxEmail, isSharedMailbox });

      logInfo('Secure Before You Click', { withMime: mime !== undefined });

      secureBeforeYouClick(
        {
          sender_email: senderEmail,
          sender_name: senderName,
          recipient_email: recipientEmail,
          recipient_name: recipientName,
          received_at: date,
          subject,
          body: encodedBody,
          ...(mime !== undefined && { mime }),
        },
        {
          onSuccess: ({ data: response }) => {
            if (!response.data.url) {
              return;
            }

            // A blocked popup answers null. That outcome is a dimension, not a reason to
            // discard the measurement: this runs from a network callback, and browsers only
            // grant popups for a few seconds after the click, so the block happens precisely
            // when the response was slow. Gating on the handle would drop every duration past
            // that window and leave the percentiles describing only the fast requests.
            // Filter `opened:true` for time-to-a-view-the-user-got, or group by it for the
            // blocked rate.
            const opened = Boolean(window.open(response.data.url, '_blank'));

            stopTimingMetric(TIMING_METRICS.SBYC_DURATION, { opened });
          },
          onError: (error) => {
            const { cause, statusCode, responseBody } = diagnoseSecureBeforeYouClickError(error);

            handleError(
              new SecureBeforeYouClickError(`Secure Before You Click failed (cause: ${cause})`, {
                cause,
                statusCode,
                originalMessage: error.message,
                ...(responseBody !== undefined && { responseBody }),
              }),
              () => showToast(TOAST_KEY_BY_CAUSE[cause]),
            );
          },
        },
      );
    } finally {
      isPreparingRef.current = false;
      setIsPreparing(false);
    }
  };

  return {
    isSecureBeforeYouClickPending: isRequestPending || isPreparing,
    handleSecureBeforeYouClick,
  };
};
